"use client";
import { Fragment } from "react";
import { StyledLink } from "~/components/Link";
import { useQuery } from "~/hooks/useQuery";

interface PaginationProps {
	currentPage: number;
	totalPages: number;
	url: string;
}

export function Pagination({ currentPage, url, totalPages }: PaginationProps) {
	const emptyList = totalPages === 1;
	const listIsShort = totalPages <= 5;
	const nearBeginning = currentPage < 3;
	const nearMiddle = currentPage >= 3 && currentPage < totalPages - 2;
	const nearEnd = currentPage >= totalPages - 2;

	function calculatePagination() {
		let around: number[] = [];
		let first: number[] = [];
		let last: number[] = [];

		if (emptyList) {
			around = [1];
		} else if (listIsShort) {
			around = Array.from({ length: totalPages }, (_, i) => i + 1);
		} else if (nearBeginning) {
			around = Array.from({ length: 3 }, (_, i) => i + 1);
			last = [totalPages];
		} else if (nearMiddle) {
			first = [1];
			around = [currentPage - 1, currentPage, currentPage + 1];
			last = [totalPages];
		} else if (nearEnd) {
			first = [1];
			around = [totalPages - 2, totalPages - 1, totalPages];
		}

		return { around, first, last } as const;
	}

	const { around, first, last } = calculatePagination();

	return (
		<div className="flex items-center justify-end gap-4">
			{first.map((page, index) => (
				<PaginationItem key={index} url={url} page={page} />
			))}

			{around.length > 1 && (
				<Fragment>
					{first.length > 0 && <PaginationSpacer />}

					{around.map((page, index) => (
						<PaginationItem key={index} url={url} page={page} />
					))}

					{last.length > 0 && <PaginationSpacer />}
				</Fragment>
			)}

			{last.map((page, index) => (
				<PaginationItem key={index} url={url} page={page} />
			))}
		</div>
	);
}

function PaginationSpacer() {
	return (
		<div className="inline-flex h-10 items-end justify-center font-bold text-primary-dark text-sm">
			...
		</div>
	);
}

function PaginationItem(props: { url: string; page: number }) {
	const { createUrl, searchParams } = useQuery();

	const isActive = searchParams.has("page")
		? searchParams.get("page") === props.page.toString()
		: props.page === 1;

	const href = createUrl({ page: props.page.toString() });

	return (
		<StyledLink
			href={href}
			title={`page ${props.page}`}
			className="inline-flex h-10 w-10 items-center justify-center"
			variant={isActive ? "contained" : "outlined"}
		>
			{props.page}
		</StyledLink>
	);
}
